import { motion } from "framer-motion";
import { Shield, Brain, Crosshair } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
};

const pillars = [
  {
    num: "01",
    title: "Real-World Utility",
    body: "No sports, no performance, no competition. Built for real scenarios where rules do not exist.",
    icon: Shield,
    img: "/images/real-world.png",
    imgAlt: "Real world combat stance",
    imgPos: "object-[50%_20%]",
  },
  {
    num: "02",
    title: "Mental Sharpness",
    body: "Awareness, decision-making under stress, controlled aggression. Skills that transfer beyond the mat.",
    icon: Brain,
    img: "/images/mental-sharpness.png",
    imgAlt: "Mental sharpness and focus",
    imgPos: "object-[50%_20%]",
  },
  {
    num: "03",
    title: "Identity",
    body: "The kind of person who trains Krav is switched on and serious. Absolute confidence in every move.",
    icon: Crosshair,
    img: "/images/identity.png",
    imgAlt: "Identity and self-defense",
    imgPos: "object-[50%_15%]",
  },
];

export const WhyTrainSection = () => {
  return (
    <section
      id="why-train"
      data-testid="why-train-section"
      className="relative px-6 md:px-16 lg:px-24 py-12 md:py-16"
    >
      <div className="section-divider mb-10 md:mb-14" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <motion.div {...fadeUp}>
          <div className="brass-line mb-4" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-sig font-['IBM_Plex_Sans'] block mb-3">
            Why Here
          </span>
          <h2
            data-testid="why-train-headline"
            className="font-['Oswald'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-ink"
          >
            Why Train <span className="text-sig">Here?</span>
          </h2>
        </motion.div>
        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="text-sm leading-relaxed text-mute font-['IBM_Plex_Sans'] font-light max-w-sm"
        >
          A dedicated space built for one thing — making you capable.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {pillars.map((pillar, i) => (
          <motion.div
            key={pillar.num}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: i * 0.1 }}
            data-testid={`pillar-${pillar.num}`}
            className="relative group"
          >
            <div className="relative overflow-hidden aspect-[4/3]">
              <img
                src={pillar.img}
                alt={pillar.imgAlt}
                className={`w-full h-full object-cover ${pillar.imgPos} group-hover:scale-105 transition-transform duration-700`}
              />
              <div className="absolute inset-0 bg-page/20 group-hover:bg-transparent transition-colors duration-500" />
              <span className="absolute top-3 left-3 font-['Oswald'] text-4xl font-bold text-sig/25">
                {pillar.num}
              </span>
            </div>
            <div className="pt-3">
              <div className="flex items-center gap-2 mb-2">
                <pillar.icon className="w-3.5 h-3.5 text-sig" strokeWidth={1.5} />
                <h3 className="font-['Oswald'] text-base md:text-lg font-bold uppercase tracking-wide text-ink group-hover:text-sig transition-colors duration-300">
                  {pillar.title}
                </h3>
              </div>
              <p className="text-xs leading-[1.6] text-mute font-['IBM_Plex_Sans'] font-light">
                {pillar.body}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
